import type { ComponentType } from 'react';
import { defineMessages } from 'react-intl';
import type { IconProps } from '@phosphor-icons/react';
import {
  SquaresFour,
  TestTube,
  Network,
  ChartBar,
  CurrencyDollar,
  Robot,
  Database,
  TerminalWindow,
  Pulse,
  EnvelopeSimple,
  Compass,
  Handshake,
  Storefront,
  Key,
  Flask,
  PaperPlaneTilt,
  ClockCounterClockwise,
  FrameCorners,
  DownloadSimple,
  ChartLineUp,
} from '@phosphor-icons/react';

/**
 * Category taxonomy for grouping the tools below (2026-09-06 reorg). Used by the hub page
 * (section headers over the grid), by BackofficeToolPicker (group headings) and by BackofficeNav
 * (the one-click row of the current tool's category siblings). Order here is display order.
 */
export const BACKOFFICE_TOOL_CATEGORIES = [
  { id: 'crawl', labelKey: 'backoffice.category.crawl', defaultLabel: 'Crawl & Pipeline' },
  { id: 'content', labelKey: 'backoffice.category.content', defaultLabel: 'Content & SEO' },
  { id: 'traffic', labelKey: 'backoffice.category.traffic', defaultLabel: 'Traffic & Observability' },
  { id: 'engineering', labelKey: 'backoffice.category.engineering', defaultLabel: 'Engineering Tools' },
  { id: 'business', labelKey: 'backoffice.category.business', defaultLabel: 'Support & Business' },
] as const;

export type BackofficeCategoryId = (typeof BACKOFFICE_TOOL_CATEGORIES)[number]['id'];

export type BackofficeNavItem = {
  path: string;
  labelKey: string;
  defaultLabel: string;
  descriptionKey: string;
  defaultDescription: string;
  icon: ComponentType<IconProps>;
  category: BackofficeCategoryId;
};

/**
 * Main tools linked from the hub, the top nav and the tool picker. Order is grouped by category
 * (see BACKOFFICE_TOOL_CATEGORIES above): keep new entries next to their category's existing ones
 * rather than appending to the end. The one deliberate exception is Outreach CRM, pinned first
 * for historical reasons; every consumer filters by category id, not array position, so it still
 * lands in its "business" group.
 */
export const BACKOFFICE_TOOL_ROUTES: BackofficeNavItem[] = [
  {
    path: '/backoffice/outreach-crm',
    labelKey: 'backoffice.outreachCrm.nav',
    defaultLabel: 'Outreach CRM',
    descriptionKey: 'backoffice.hub.desc.outreachCrm',
    defaultDescription: 'Draft, preview, and send outreach email; track status, opens, and follow-ups.',
    icon: PaperPlaneTilt,
    category: 'business',
  },

  // Crawl & Pipeline
  {
    path: '/backoffice/dashboard',
    labelKey: 'backoffice.dashboard',
    defaultLabel: 'Dashboard',
    descriptionKey: 'backoffice.hub.desc.dashboard',
    defaultDescription: 'Manage company crawls, pipeline health, and crawl tooling.',
    icon: SquaresFour,
    category: 'crawl',
  },
  {
    path: '/backoffice/pipeline-logs',
    labelKey: 'backoffice.pipelineLogs.nav',
    defaultLabel: 'Pipeline logs',
    descriptionKey: 'backoffice.hub.desc.pipelineLogs',
    defaultDescription: 'Live tail of crawl, enrich, and indexing Lambda logs.',
    icon: TerminalWindow,
    category: 'crawl',
  },
  {
    path: '/backoffice/search',
    labelKey: 'backoffice.search.nav',
    defaultLabel: 'Search cluster',
    descriptionKey: 'backoffice.hub.desc.search',
    defaultDescription: 'Typesense collection health and live search inspector.',
    icon: Database,
    category: 'crawl',
  },

  // Content & SEO
  {
    path: '/backoffice/job-data-explorer',
    labelKey: 'backoffice.jobDataExplorer.nav',
    defaultLabel: 'Job data API',
    descriptionKey: 'backoffice.hub.desc.jobDataApi',
    defaultDescription: 'Inspect job records and build Claude prompts.',
    icon: Robot,
    category: 'content',
  },
  {
    path: '/backoffice/salary-stats',
    labelKey: 'backoffice.dataExplorer.nav',
    defaultLabel: 'Data explorer',
    descriptionKey: 'backoffice.hub.desc.dataExplorer',
    defaultDescription: 'Explore salary and listing data with charts for content.',
    icon: CurrencyDollar,
    category: 'content',
  },
  {
    path: '/backoffice/owned-brands',
    labelKey: 'backoffice.ownedBrands.nav',
    defaultLabel: 'Owned brands',
    descriptionKey: 'backoffice.hub.desc.ownedBrands',
    defaultDescription: 'Approve or reject LLM-extracted sub-brands before they reach the public profile.',
    icon: Storefront,
    category: 'content',
  },
  {
    path: '/backoffice/occupation-exposure',
    labelKey: 'backoffice.occupationExposure.nav',
    defaultLabel: 'Occupation exposure',
    descriptionKey: 'backoffice.hub.desc.occupationExposure',
    defaultDescription: 'Review AI/automation-exposure scores before they publish on role pages (TEC-485).',
    icon: ChartLineUp,
    category: 'content',
  },
  {
    path: '/backoffice/srp-indexability',
    labelKey: 'backoffice.srpIndexability.nav',
    defaultLabel: 'SRP indexability',
    descriptionKey: 'backoffice.hub.desc.srpIndexability',
    defaultDescription: 'Check live index/noindex and robots.txt decisions for any SRP URL.',
    icon: Compass,
    category: 'content',
  },
  {
    path: '/backoffice/frame-kit',
    labelKey: 'backoffice.frameKit.nav',
    defaultLabel: 'Frame Kit',
    descriptionKey: 'backoffice.hub.desc.frameKit',
    defaultDescription: 'Make social and marketing graphics from a screenshot and a headline, export as PNG.',
    icon: FrameCorners,
    category: 'content',
  },

  // Traffic & Observability
  {
    path: '/backoffice/dashboard/user-agents',
    labelKey: 'backoffice.userAgents.nav',
    defaultLabel: 'User agents',
    descriptionKey: 'backoffice.hub.desc.userAgents',
    defaultDescription: 'Traffic, CloudFront, WAF, and user-agent analytics.',
    icon: ChartBar,
    category: 'traffic',
  },
  {
    path: '/backoffice/monitoring',
    labelKey: 'backoffice.monitoring.nav',
    defaultLabel: 'Monitoring',
    descriptionKey: 'backoffice.hub.desc.monitoring',
    defaultDescription: 'CloudWatch logs and Lambda metrics for the user-facing app.',
    icon: Pulse,
    category: 'traffic',
  },
  {
    path: '/backoffice/email-alerts',
    labelKey: 'backoffice.emailAlerts.nav',
    defaultLabel: 'Email alerts',
    descriptionKey: 'backoffice.hub.desc.emailAlerts',
    defaultDescription: 'Monitor job-alert sends, bounces, complaints, and unsubscribes.',
    icon: EnvelopeSimple,
    category: 'traffic',
  },

  // Engineering Tools
  {
    path: '/backoffice/experiments',
    labelKey: 'backoffice.experiments.nav',
    defaultLabel: 'Feature flags & experiments',
    descriptionKey: 'backoffice.hub.desc.experiments',
    defaultDescription: 'Toggle personal feature-flag cookies, and start/pause/stop live A/B tests and fake-door flags.',
    icon: Flask,
    category: 'engineering',
  },
  {
    path: '/backoffice/testing',
    labelKey: 'backoffice.testing',
    defaultLabel: 'Testing',
    descriptionKey: 'backoffice.hub.desc.testing',
    defaultDescription: 'Integration health checks and debug toggles.',
    icon: TestTube,
    category: 'engineering',
  },
  {
    path: '/backoffice/mcp-keys',
    labelKey: 'backoffice.mcpKeys.nav',
    defaultLabel: 'API keys',
    descriptionKey: 'backoffice.hub.desc.mcpKeys',
    defaultDescription: 'Mint internal-scope MCP API keys for ops/analytics tooling.',
    icon: Key,
    category: 'engineering',
  },
  {
    path: '/backoffice/architecture',
    labelKey: 'backoffice.architecture',
    defaultLabel: 'Architecture',
    descriptionKey: 'backoffice.hub.desc.architecture',
    defaultDescription: 'System infrastructure and data-flow overview.',
    icon: Network,
    category: 'engineering',
  },

  // Support & Business
  // Feedback (/backoffice/dashboard/feedback) intentionally has no nav entry anymore: its inbound
  // email + in-app feedback are now managed from the Outreach CRM's Inbox tab (InboxView.tsx),
  // which reuses that page's own /api/feedback backend as-is. The route itself still works (a
  // banner there points here) so nothing that links to it directly breaks.
  {
    path: '/backoffice/business-development',
    labelKey: 'backoffice.businessDevelopment.nav',
    defaultLabel: 'Business Development',
    descriptionKey: 'backoffice.hub.desc.businessDevelopment',
    defaultDescription: 'Monetisation plans, strategy notes, and market research for reference.',
    icon: Handshake,
    category: 'business',
  },
  {
    path: '/backoffice/activity-replay',
    labelKey: 'backoffice.activityReplay.nav',
    defaultLabel: 'Activity replay',
    descriptionKey: 'backoffice.hub.desc.activityReplay',
    defaultDescription: "Rewind and replay one user's tracked activity on a visual timeline.",
    icon: ClockCounterClockwise,
    category: 'business',
  },
  {
    path: '/backoffice/data-export',
    labelKey: 'backoffice.dataExport.nav',
    defaultLabel: 'Data export',
    descriptionKey: 'backoffice.hub.desc.dataExport',
    defaultDescription: "Fulfill a GDPR access or portability request by exporting one user's stored data as JSON.",
    icon: DownloadSimple,
    category: 'business',
  },
];

/**
 * Tool names, descriptions and category headings are looked up dynamically (`formatMessage({ id:
 * tool.labelKey, ... })` in BackofficeNav, BackofficeToolPicker and the hub page), so
 * `extract:i18n`'s static extractor can't see them at the call sites. This block is the extraction
 * source that keeps every backoffice.category.* / backoffice.hub.desc.* / tool-label id in the
 * catalogue (same pattern as ResultsPerPageSelector). When you add or reword a tool above, add or
 * update its entries here too, with the same id and English text.
 */
export const backofficeToolMessages = defineMessages({
  categoryCrawl: {
    id: 'backoffice.category.crawl',
    defaultMessage: 'Crawl & Pipeline',
    description: 'Section heading grouping related backoffice tools in the tool picker and on the all-tools page',
  },
  categoryContent: {
    id: 'backoffice.category.content',
    defaultMessage: 'Content & SEO',
    description: 'Section heading grouping related backoffice tools in the tool picker and on the all-tools page',
  },
  categoryTraffic: {
    id: 'backoffice.category.traffic',
    defaultMessage: 'Traffic & Observability',
    description: 'Section heading grouping related backoffice tools in the tool picker and on the all-tools page',
  },
  categoryEngineering: {
    id: 'backoffice.category.engineering',
    defaultMessage: 'Engineering Tools',
    description: 'Section heading grouping related backoffice tools in the tool picker and on the all-tools page',
  },
  categoryBusiness: {
    id: 'backoffice.category.business',
    defaultMessage: 'Support & Business',
    description: 'Section heading grouping related backoffice tools in the tool picker and on the all-tools page',
  },
  outreachCrmNav: {
    id: 'backoffice.outreachCrm.nav',
    defaultMessage: 'Outreach CRM',
    description: 'Navigation link to the outreach CRM for backlink and employer BD tracking',
  },
  hubDescOutreachCrm: {
    id: 'backoffice.hub.desc.outreachCrm',
    defaultMessage: 'Draft, preview, and send outreach email; track status, opens, and follow-ups.',
    description: 'One-line description of what a backoffice tool does, shown under its name in the tool picker and on the all-tools page',
  },
  dashboard: {
    id: 'backoffice.dashboard',
    defaultMessage: 'Dashboard',
    description: 'Navigation link to the admin dashboard for managing companies and crawls',
  },
  hubDescDashboard: {
    id: 'backoffice.hub.desc.dashboard',
    defaultMessage: 'Manage company crawls, pipeline health, and crawl tooling.',
    description: 'One-line description of what a backoffice tool does, shown under its name in the tool picker and on the all-tools page',
  },
  pipelineLogsNav: {
    id: 'backoffice.pipelineLogs.nav',
    defaultMessage: 'Pipeline logs',
    description: 'Name of a backoffice tool, shown in the top nav, the tool picker and the all-tools page',
  },
  hubDescPipelineLogs: {
    id: 'backoffice.hub.desc.pipelineLogs',
    defaultMessage: 'Live tail of crawl, enrich, and indexing Lambda logs.',
    description: 'One-line description of what a backoffice tool does, shown under its name in the tool picker and on the all-tools page',
  },
  searchNav: {
    id: 'backoffice.search.nav',
    defaultMessage: 'Search cluster',
    description: 'Name of a backoffice tool, shown in the top nav, the tool picker and the all-tools page',
  },
  hubDescSearch: {
    id: 'backoffice.hub.desc.search',
    defaultMessage: 'Typesense collection health and live search inspector.',
    description: 'One-line description of what a backoffice tool does, shown under its name in the tool picker and on the all-tools page',
  },
  jobDataExplorerNav: {
    id: 'backoffice.jobDataExplorer.nav',
    defaultMessage: 'Job data API',
    description: 'Navigation link to job data API and Claude prompt',
  },
  hubDescJobDataApi: {
    id: 'backoffice.hub.desc.jobDataApi',
    defaultMessage: 'Inspect job records and build Claude prompts.',
    description: 'One-line description of what a backoffice tool does, shown under its name in the tool picker and on the all-tools page',
  },
  dataExplorerNav: {
    id: 'backoffice.dataExplorer.nav',
    defaultMessage: 'Data explorer',
    description: 'Navigation link to data explorer',
  },
  hubDescDataExplorer: {
    id: 'backoffice.hub.desc.dataExplorer',
    defaultMessage: 'Explore salary and listing data with charts for content.',
    description: 'One-line description of what a backoffice tool does, shown under its name in the tool picker and on the all-tools page',
  },
  ownedBrandsNav: {
    id: 'backoffice.ownedBrands.nav',
    defaultMessage: 'Owned brands',
    description: 'Name of a backoffice tool, shown in the top nav, the tool picker and the all-tools page',
  },
  hubDescOwnedBrands: {
    id: 'backoffice.hub.desc.ownedBrands',
    defaultMessage: 'Approve or reject LLM-extracted sub-brands before they reach the public profile.',
    description: 'One-line description of what a backoffice tool does, shown under its name in the tool picker and on the all-tools page',
  },
  occupationExposureNav: {
    id: 'backoffice.occupationExposure.nav',
    defaultMessage: 'Occupation exposure',
    description: 'Name of a backoffice tool, shown in the top nav, the tool picker and the all-tools page',
  },
  hubDescOccupationExposure: {
    id: 'backoffice.hub.desc.occupationExposure',
    defaultMessage: 'Review AI/automation-exposure scores before they publish on role pages (TEC-485).',
    description: 'One-line description of what a backoffice tool does, shown under its name in the tool picker and on the all-tools page',
  },
  srpIndexabilityNav: {
    id: 'backoffice.srpIndexability.nav',
    defaultMessage: 'SRP indexability',
    description: 'Name of a backoffice tool, shown in the top nav, the tool picker and the all-tools page',
  },
  hubDescSrpIndexability: {
    id: 'backoffice.hub.desc.srpIndexability',
    defaultMessage: 'Check live index/noindex and robots.txt decisions for any SRP URL.',
    description: 'One-line description of what a backoffice tool does, shown under its name in the tool picker and on the all-tools page',
  },
  frameKitNav: {
    id: 'backoffice.frameKit.nav',
    defaultMessage: 'Frame Kit',
    description: 'Navigation link to the Frame Kit graphic maker for social and marketing images',
  },
  hubDescFrameKit: {
    id: 'backoffice.hub.desc.frameKit',
    defaultMessage: 'Make social and marketing graphics from a screenshot and a headline, export as PNG.',
    description: 'One-line description of what a backoffice tool does, shown under its name in the tool picker and on the all-tools page',
  },
  userAgentsNav: {
    id: 'backoffice.userAgents.nav',
    defaultMessage: 'User agents',
    description: 'Navigation link to user agent analysis',
  },
  hubDescUserAgents: {
    id: 'backoffice.hub.desc.userAgents',
    defaultMessage: 'Traffic, CloudFront, WAF, and user-agent analytics.',
    description: 'One-line description of what a backoffice tool does, shown under its name in the tool picker and on the all-tools page',
  },
  monitoringNav: {
    id: 'backoffice.monitoring.nav',
    defaultMessage: 'Monitoring',
    description: 'Name of a backoffice tool, shown in the top nav, the tool picker and the all-tools page',
  },
  hubDescMonitoring: {
    id: 'backoffice.hub.desc.monitoring',
    defaultMessage: 'CloudWatch logs and Lambda metrics for the user-facing app.',
    description: 'One-line description of what a backoffice tool does, shown under its name in the tool picker and on the all-tools page',
  },
  emailAlertsNav: {
    id: 'backoffice.emailAlerts.nav',
    defaultMessage: 'Email alerts',
    description: 'Navigation link to the job-alert email compliance monitoring dashboard',
  },
  hubDescEmailAlerts: {
    id: 'backoffice.hub.desc.emailAlerts',
    defaultMessage: 'Monitor job-alert sends, bounces, complaints, and unsubscribes.',
    description: 'One-line description of what a backoffice tool does, shown under its name in the tool picker and on the all-tools page',
  },
  experimentsNav: {
    id: 'backoffice.experiments.nav',
    defaultMessage: 'Feature flags & experiments',
    description: 'Navigation link to the feature flags and experiments admin page',
  },
  hubDescExperiments: {
    id: 'backoffice.hub.desc.experiments',
    defaultMessage: 'Toggle personal feature-flag cookies, and start/pause/stop live A/B tests and fake-door flags.',
    description: 'One-line description of what a backoffice tool does, shown under its name in the tool picker and on the all-tools page',
  },
  testing: {
    id: 'backoffice.testing',
    defaultMessage: 'Testing',
    description: 'Navigation link to the testing tools page',
  },
  hubDescTesting: {
    id: 'backoffice.hub.desc.testing',
    defaultMessage: 'Integration health checks and debug toggles.',
    description: 'One-line description of what a backoffice tool does, shown under its name in the tool picker and on the all-tools page',
  },
  mcpKeysNav: {
    id: 'backoffice.mcpKeys.nav',
    defaultMessage: 'API keys',
    description: 'Navigation link to internal-scope MCP API key management',
  },
  hubDescMcpKeys: {
    id: 'backoffice.hub.desc.mcpKeys',
    defaultMessage: 'Mint internal-scope MCP API keys for ops/analytics tooling.',
    description: 'One-line description of what a backoffice tool does, shown under its name in the tool picker and on the all-tools page',
  },
  architecture: {
    id: 'backoffice.architecture',
    defaultMessage: 'Architecture',
    description: 'Navigation link to the architecture documentation page',
  },
  hubDescArchitecture: {
    id: 'backoffice.hub.desc.architecture',
    defaultMessage: 'System infrastructure and data-flow overview.',
    description: 'One-line description of what a backoffice tool does, shown under its name in the tool picker and on the all-tools page',
  },
  businessDevelopmentNav: {
    id: 'backoffice.businessDevelopment.nav',
    defaultMessage: 'Business Development',
    description: 'Name of a backoffice tool, shown in the top nav, the tool picker and the all-tools page',
  },
  hubDescBusinessDevelopment: {
    id: 'backoffice.hub.desc.businessDevelopment',
    defaultMessage: 'Monetisation plans, strategy notes, and market research for reference.',
    description: 'One-line description of what a backoffice tool does, shown under its name in the tool picker and on the all-tools page',
  },
  activityReplayNav: {
    id: 'backoffice.activityReplay.nav',
    defaultMessage: 'Activity replay',
    description: 'Name of a backoffice tool, shown in the top nav, the tool picker and the all-tools page',
  },
  hubDescActivityReplay: {
    id: 'backoffice.hub.desc.activityReplay',
    defaultMessage: "Rewind and replay one user's tracked activity on a visual timeline.",
    description: 'One-line description of what a backoffice tool does, shown under its name in the tool picker and on the all-tools page',
  },
  dataExportNav: {
    id: 'backoffice.dataExport.nav',
    defaultMessage: 'Data export',
    description: 'Name of a backoffice tool, shown in the top nav, the tool picker and the all-tools page',
  },
  hubDescDataExport: {
    id: 'backoffice.hub.desc.dataExport',
    defaultMessage: "Fulfill a GDPR access or portability request by exporting one user's stored data as JSON.",
    description: 'One-line description of what a backoffice tool does, shown under its name in the tool picker and on the all-tools page',
  },
});
